import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { catchError, forkJoin, of, take } from 'rxjs';
import Swal from 'sweetalert2';

import { ProductsService } from '@app/sales/products/products.service';
import { ValuesService } from '@app/common/values/values.service';
import { AlertService } from '@app/shared/alert.service';
import { Partner } from '@app/shared/_models/common';
import { BillingTriggerEvent, Product, RentalTerms } from '@app/shared/_models/product';
import { ScanResult, looksLikeFreeText, scanLabel, scanRejection, splitQtyPrefix } from '@app/shared/_models/scan';
import { C_VALUES_UOM } from '@app/shared/constants';
import { SearchProductComponent } from '@app/shared/search-product/search-product.component';
import { serverMessage } from '@app/sales/rental-billing/rental-billing.service';
import { money } from '@app/sales/rental-billing/rental-days';

import { PresentPatient, matchingPatients, samePatient } from '../rental-contract.model';
import { AddFormState, LinkedSale, ReturnFormState, addFormProblem, basePriceTaxx, emptyAddForm, emptyReturnForm, isConfigurableProduct, isRentalTermInError, linkedProductsFor, linkedSaleOf, rentalVat, returnFormProblem, salePrices, ttcOf } from '../rental-contract.lines';
import { vitrineHidesTerms } from '../vitrine-rental-pick/vitrine-rental-pick.component';   // TEMPORAIRE (La Vitrine Médicale)

// Carte « Livrer un matériel » (ou « Reprise de matériel » = reprise hors contrat, saisie émise par `saveReturn`) de la page du dossier ; l'hôte enregistre la saisie émise par `save`.
@Component({
  selector: 'app-rental-add-line',
  templateUrl: './rental-add-line.component.html',
  styleUrls: ['./rental-add-line.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SearchProductComponent, FormsModule, MatAutocompleteModule, MatButtonModule, MatButtonToggleModule, MatCheckboxModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
})
export class RentalAddLineComponent {
  private productsService = inject(ProductsService);
  private valuesService = inject(ValuesService);
  private alertService = inject(AlertService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input() customer: Partner | null = null;   // ses tarifs servent à l'article et aux produits liés
  @Input() isHt = false;
  @Input() periodNames: Record<number, string> = {};
  @Input() present: PresentPatient[] = [];   // patients déjà au dossier, proposés sous le champ Patient
  @Input() saving = false;
  @Input() vitrinePayerId?: number | string | null;   // TEMPORAIRE (vitrine-rental-pick) : payeur facturé, null = le patient paie, absent (vue du payeur) = aucun contrat caché
  @Output() save = new EventEmitter<AddFormState>();
  @Output() saveReturn = new EventEmitter<ReturnFormState>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('searchProduct') private searchProduct?: SearchProductComponent;

  form: AddFormState = emptyAddForm();
  ret: ReturnFormState = emptyReturnForm();
  mode: 'deliver' | 'return' = 'deliver';
  readonly m = money;

  private set(form: AddFormState): void {
    this.form = form;
    this.cdr.markForCheck();   // lectures du serveur hors événement du gabarit : l'hôte OnPush de la vue du payeur ne se revérifie pas seul
  }

  // `memo` : + d'un patient du Planning ; la carte est alors ramenée à l'écran.
  start(memo?: string): void {
    if (memo !== undefined) { this.set({ ...this.form, memo }); this.ret = { ...this.ret, memo }; }
    setTimeout(() => {
      if (memo !== undefined) this.host.nativeElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      this.searchProduct?.focus();
    });
  }

  get taxLabel(): string {
    return this.isHt ? 'HT' : 'TTC';
  }

  periodName(id: number | null): string {
    return (id != null ? this.periodNames[id] : '')?.toLowerCase() || 'période';
  }

  plural(n: number, one: string, many: string): string {
    return `${n} ${n > 1 ? many : one}`;
  }

  private get pricelists(): any {
    return this.customer?.data?.pricelists ?? this.customer?.lnk_partnerprices?.map((spp: { id: any }) => spp.id);
  }

  // Branchements de RentgeneralComponent : la lecture part telle quelle à /search/scan, un texte libre est ignoré.
  onSearchBlur(text: string): void {
    if (!text) return;
    const { raw } = splitQtyPrefix(text.toString().trim());
    if (!raw) return;
    this.productsService.search_scan(raw, this.pricelists).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: ScanResult) => {
        const rejection = scanRejection(res.scan, raw);
        if (res.ppv?.lnk_product) this.handleProduct(Product.from(res.ppv.lnk_product));
        else if (rejection) { this.alertService.warn(rejection); this.searchProduct?.focus(); }
        else if (!looksLikeFreeText(res.scan, raw)) { this.alertService.warn('Code barre non trouvé : ' + scanLabel(res.scan, raw)); this.searchProduct?.focus(); }
      },
      error: (e) => { this.alertService.error('ERREUR Serveur : ' + serverMessage(e)); this.searchProduct?.focus(); },
    });
  }

  onSelectProduct(data: any): void {
    if (!data) { this.clearProduct(); return; }   // croix de la recherche (et son focus()) : émet undefined
    const productId = data.hasOwnProperty('ppv') ? data.ppv?.product : data.id;
    if (!productId) return;
    this.productsService.getProductById(productId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (raw: any) => this.handleProduct(Product.from(raw)),
      error: (e) => { this.alertService.error('ERREUR Serveur : ' + serverMessage(e)); this.searchProduct?.focus(); },
    });
  }

  // TEMPORAIRE (vitrine-rental-pick) : contrats de l'article moins ceux que le payeur facturé cache ; sans payeur transmis, tous.
  get visibleTerms(): RentalTerms[] {
    const all = this.form.product?.lnk_rental_terms ?? [];
    return this.vitrinePayerId === undefined ? all : all.filter((rt) => !vitrineHidesTerms(rt.name, this.vitrinePayerId ?? null));
  }

  // Le patient saisi suit la bascule ; l'article, propre à chaque saisie, est à rechoisir.
  setMode(mode: 'deliver' | 'return'): void {
    if (mode === this.mode) return;
    const memo = this.mode === 'deliver' ? this.form.memo : this.ret.memo;
    this.mode = mode;
    this.form = emptyAddForm(memo);
    this.ret = emptyReturnForm(memo);
    this.cdr.markForCheck();
    setTimeout(() => this.searchProduct?.focus());
  }

  private handleProduct(product: Product): void {
    if (this.mode === 'return') { this.handleReturnProduct(product); return; }
    const form = this.form;
    if (form.product?.id === product.id) return;   // la lecture tardive du blur ne défait pas le contrat déjà choisi
    if (!product.lnk_rental_terms?.length) { this.alertService.warn("Ce produit n'a pas de contrat de location"); this.searchProduct?.focus(); return; }
    if (isConfigurableProduct(product)) { this.alertService.warn("Article configurable : sa livraison se saisit dans l'écran actuel, qui ouvre la configuration"); this.searchProduct?.focus(); return; }
    this.set({ ...emptyAddForm(form.memo), product });
  }

  // L'article choisi s'en va avec ses contrats, prix et produits liés ; le patient saisi reste.
  private clearProduct(): void {
    if (this.mode === 'return') {
      if (this.ret.product) { this.ret = { ...this.ret, product: null }; this.cdr.markForCheck(); }
    } else if (this.form.product) {
      this.set(emptyAddForm(this.form.memo));
    }
  }

  // Tout article se reprend, loué ou non : la reprise ne vise aucun contrat.
  private handleReturnProduct(product: Product): void {
    if (this.ret.product?.id === product.id) return;
    if (isConfigurableProduct(product)) { this.alertService.warn("Article configurable : sa reprise se saisit dans l'écran actuel"); this.searchProduct?.focus(); return; }
    this.ret = { ...this.ret, product };
    this.cdr.markForCheck();
  }

  get patientText(): string {
    return (this.mode === 'return' ? this.ret.memo : this.form.memo) ?? '';
  }

  get returnProblem(): string | null {
    return returnFormProblem(this.ret);
  }

  submitReturn(): void {
    if (this.saving || returnFormProblem(this.ret)) return;
    this.saveReturn.emit(this.ret);
  }

  termsError(rt: RentalTerms): boolean {
    return isRentalTermInError(rt);
  }

  termsPrice(rt: RentalTerms): string | null {
    const base = basePriceTaxx(rt);
    if (base === null) return null;
    if (rt.vat_rate == null) return `${money(base)} € HT · TVA du contrat absente`;
    return `${money(base)} € HT · ${money(ttcOf(base, Number(rt.vat_rate) || 0))} € TTC`;
  }

  selectTerms(rt: RentalTerms): void {
    const form = this.form;
    if (!form.product) return;
    if (isRentalTermInError(rt)) {
      this.alertService.error(`Impossible de sélectionner ce contrat pour facturation : ${rt.error || 'Tarif de référence LPP manquant ou inactif'}`);
      return;
    }
    const vat = rentalVat(rt);
    if (typeof vat === 'string') { this.alertService.error(vat); return; }
    const base = basePriceTaxx(rt);
    const linked = linkedProductsFor(rt, BillingTriggerEvent.DELIVERY);
    this.set({
      ...form, terms: rt, vat: vat.vat, vatRate: vat.vatRate, hasTier: base !== null, priceTouched: false, duration: null, unlimited: true,
      price: base === null ? null : this.isHt ? base : ttcOf(base, vat.vatRate),
      linked: linked.length ? { loading: true, items: [], uoms: [] } : null,
    });
    if (linked.length) this.loadLinked(this.form, linked);
  }

  // Produits liés lus comme l'écran actuel (addLinkedProducts : index jpid, tarifs du client) ; un produit illisible bloque l'enregistrement, il n'est pas sauté.
  private loadLinked(form: AddFormState, linked: { product_id: number; quantity: number; billing_trigger: any }[]): void {
    forkJoin([
      forkJoin(linked.map((lp) => this.productsService.search_productpackaging('jpid', lp.product_id, this.pricelists).pipe(catchError(() => of(null))))),
      this.valuesService.getValues$(C_VALUES_UOM).pipe(take(1), catchError(() => of([]))),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(([ppvs, uoms]) => {
      if (this.form !== form) return;
      this.set({ ...form, linked: { loading: false, items: linked.map((lp, i) => linkedSaleOf(lp, ppvs[i])), uoms: uoms ?? [] } });
    });
  }

  linkedPrice(it: LinkedSale): string {
    const pr = salePrices(it.ppv);
    return `${money((this.isHt ? pr.unetTaxx : pr.unetTaxi) * it.quantity)} € ${this.taxLabel}`;
  }

  changeTerms(): void {
    this.set({ ...this.form, terms: null, vat: null, price: null, priceTouched: false, linked: null });
  }

  get patientOptions(): PresentPatient[] {
    return matchingPatients(this.present, this.patientText);
  }

  get patientMatch(): { patient: PresentPatient; exact: boolean } | null {
    return samePatient(this.present, this.patientText);
  }

  get problem(): string | null {
    return addFormProblem(this.form);
  }

  // Un produit lié non vendable se vend après confirmation, comme le NotSellingDialog de l'écran actuel.
  async submit(): Promise<void> {
    const p = this.form;
    if (this.saving || addFormProblem(p)) return;
    const notSold = (p.linked?.items ?? []).filter((it) => it.notSold);
    if (notSold.length) {
      const ok = await Swal.fire({
        title: 'Produit non vendable', icon: 'warning', showCancelButton: true, focusCancel: true, confirmButtonText: 'Vendre quand même', cancelButtonText: 'Annuler',
        text: `Ce contrat ajoute à la livraison ${notSold.map((it) => it.name).join(', ')}, marqué non vendable. Le vendre quand même ?`,
      });
      if (!ok.isConfirmed || this.form !== p) return;
    }
    this.save.emit(p);
  }
}
